/**
 * Was this `SIGNED_IN` a sign-up, a login, or neither?
 *
 * Supabase raises `SIGNED_IN` far more often than anyone signs in: on every
 * page load that restores a stored session, when a hidden tab becomes visible
 * again, and in every other open tab of the site (its cross-tab broadcast).
 * Treating each one as a login made GA4's `login` a count of opened tabs — the
 * owner's account logged 11 "logins" in two days without signing in once —
 * and counting every one inside the new-account window reported one Google
 * sign-up four times and sent that person two welcome emails.
 *
 * The answer comes from the server's own timestamps, not from the event:
 *
 * - `last_sign_in_at` moves only when credentials are exchanged (a password,
 *   the OAuth callback, an email-confirmation link) — never on a token
 *   refresh. A stale one means this is a restored session: nothing happened.
 * - A sign-in that *is* the account's first — `last_sign_in_at` within a minute
 *   of `email_confirmed_at` (or of `created_at` when there is none) — is the
 *   sign-up. Both are server clocks, so a skewed device clock cannot turn a
 *   login into a sign-up. This also counts an email sign-up at the moment its
 *   confirmation link is clicked, which the old "created within 30 s" rule
 *   missed whenever someone took longer than that to open their inbox.
 */

import type { User } from "@supabase/supabase-js";

export type AuthMoment = "sign_up" | "login";

/**
 * How recent `last_sign_in_at` must be, by the device clock, to be this page's
 * doing. Long enough for a slow phone to load the app after the OAuth
 * redirect; short enough that returning from Stripe a few minutes later is not
 * mistaken for signing in again (and that tab is also caught by the per-tab
 * key below).
 */
export const AUTH_MOMENT_WINDOW_MS = 60_000;

type AuthUser = Pick<User, "id" | "created_at" | "last_sign_in_at" | "email_confirmed_at"> &
  Partial<Pick<User, "user_metadata">>;

/**
 * Set on an account the Stripe webhook made for a guest checkout, once its
 * first sign-in has been reported. See `classifyAuthMoment`.
 */
export const GUEST_SIGNUP_COUNTED = "guest_signup_counted";

/** An account `stripe-webhook` created for someone who paid without signing in. */
export function isGuestCheckoutAccount(user: AuthUser): boolean {
  return user.user_metadata?.created_via === "guest_checkout";
}

function time(value: string | null | undefined): number {
  const t = value ? Date.parse(value) : NaN;
  return Number.isFinite(t) ? t : NaN;
}

export function classifyAuthMoment(user: AuthUser, now: number = Date.now()): AuthMoment | null {
  const signedInAt = time(user.last_sign_in_at);
  if (Number.isNaN(signedInAt)) return null;
  if (Math.abs(now - signedInAt) >= AUTH_MOMENT_WINDOW_MS) return null;

  const firstUsableAt = time(user.email_confirmed_at ?? user.created_at);
  const isFirst = !Number.isNaN(firstUsableAt) && Math.abs(signedInAt - firstUsableAt) < AUTH_MOMENT_WINDOW_MS;
  if (isFirst) return "sign_up";

  // A guest checkout's account is made, already confirmed, by the webhook when
  // they pay; they first sign in minutes or hours later, from the emailed link
  // or Google. No timestamp marks that as the first, so the account carries a
  // flag once it has been counted (set by useAuth after reporting).
  if (isGuestCheckoutAccount(user) && !user.user_metadata?.[GUEST_SIGNUP_COUNTED]) return "sign_up";
  return "login";
}

/** One sign-in, however many times and in however many tabs it is announced. */
export function authMomentKey(user: AuthUser): string {
  return `${user.id}:${user.last_sign_in_at ?? user.created_at}`;
}

const claimedHere = new Set<string>();
const TAB_KEY = "analytics_auth_moment";

/**
 * True for exactly one caller per sign-in.
 *
 * Three layers, each covering what the one before cannot:
 * - memory, for the repeat `SIGNED_IN` events within one page;
 * - sessionStorage, for a reload or the Stripe round trip in the same tab;
 * - a Web Lock held for the life of the tab, for the other tabs Supabase
 *   broadcasts the sign-in to. `ifAvailable` means a second tab is told "taken"
 *   immediately rather than queued.
 *
 * Where a layer is unavailable it is skipped and the claim succeeds — a rare
 * duplicate is better than losing the sign-up.
 */
export async function claimAuthMoment(key: string): Promise<boolean> {
  if (claimedHere.has(key)) return false;
  claimedHere.add(key);

  try {
    if (sessionStorage.getItem(TAB_KEY) === key) return false;
    sessionStorage.setItem(TAB_KEY, key);
  } catch {
    // Storage disabled: fall through to the lock.
  }

  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (!locks || typeof locks.request !== "function") return true;

  return new Promise<boolean>((resolve) => {
    locks
      .request(`auth-moment:${key}`, { ifAvailable: true }, (lock) => {
        resolve(lock !== null);
        // Never settles, so the lock is released only when the tab goes away.
        return lock ? new Promise<void>(() => {}) : undefined;
      })
      .catch(() => resolve(true));
  });
}

/** Reset module state. Tests only. */
export function __resetAuthMomentForTests(): void {
  claimedHere.clear();
  try {
    sessionStorage.removeItem(TAB_KEY);
  } catch {
    // ignore
  }
}
